import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';
import { auth } from './auth';
import { formatDate } from './format';
import { orgLocale } from './org-locale';
import { useOrgId } from './org';

export type EInvoiceStatus = 'not_required' | 'pending' | 'submitted' | 'accepted' | 'rejected';
export type LegalIdType = 'TL' | 'EID' | 'PAS' | 'CD';

export interface EInvoicingSettings {
  enabled: boolean;
  asp: string;
  startDate: string | null;
  legalId: string | null;
  legalIdType: LegalIdType;
  endpointId: string | null;
  missing: string[];
  updatedAt: string | null;
}

export interface EInvoiceError {
  source: 'validation' | 'asp';
  rule?: string;
  code?: string;
  term?: string;
  message: string;
}

export interface EInvoiceRow {
  submissionId: string;
  documentId: string;
  kind: 'invoice' | 'credit_note';
  number: string;
  contactId: string;
  contactName: string;
  issueDate: string;
  total: string;
  documentStatus: string;
  status: EInvoiceStatus;
  needsAttention: boolean;
  attempts: number;
  nextAttemptAt: string | null;
  aspReference: string | null;
  currentVersion: number;
  lastError: string | null;
  errors: EInvoiceError[];
  statusReason: string | null;
  updatedAt: string;
}

export interface EInvoiceEvent {
  id: string;
  at: string;
  event: string;
  fromStatus: string | null;
  toStatus: string | null;
  payloadVersion: number | null;
  detail: Record<string, unknown> | null;
  actorName: string | null;
}

export interface EInvoicePayload {
  id: string;
  version: number;
  builtAt: string;
  valid: boolean;
  sha256: string;
  idempotencyKey: string;
  findings: Array<EInvoiceError & { severity: 'error' | 'warning' }>;
}

export interface EInvoiceDetail {
  enabled: boolean;
  submission: EInvoiceRow | null;
  events: EInvoiceEvent[];
  payloads: EInvoicePayload[];
}

export const LEGAL_ID_TYPES: Array<[LegalIdType, string]> = [
  ['TL', 'Trade licence'],
  ['EID', 'Emirates ID'],
  ['PAS', 'Passport'],
  ['CD', 'Cabinet decision'],
];

/** What the chip says. A pending e-invoice whose retries are used up reads "Needs attention". */
export function einvoiceLabel(r: { status: EInvoiceStatus; needsAttention?: boolean }): string {
  if (r.status === 'pending' && r.needsAttention) return 'Needs attention';
  return {
    not_required: 'Not required',
    pending: 'Pending',
    submitted: 'Submitted',
    accepted: 'Accepted',
    rejected: 'Rejected',
  }[r.status];
}

export type ChipTone = 'muted' | 'info' | 'success' | 'warning' | 'danger';
export function einvoiceTone(r: { status: EInvoiceStatus; needsAttention?: boolean }): ChipTone {
  if (r.status === 'rejected' || r.needsAttention) return 'danger';
  if (r.status === 'accepted') return 'success';
  if (r.status === 'submitted') return 'info';
  if (r.status === 'pending') return 'warning';
  return 'muted';
}

/** On the exceptions list: rejected, or retries used up. */
export const isException = (r: EInvoiceRow): boolean => r.status === 'rejected' || r.needsAttention;

/** Resubmit is offered for rejected e-invoices and failed deliveries of documents that are not void. */
export const canResubmit = (r: EInvoiceRow): boolean =>
  isException(r) && r.documentStatus !== 'void';

/** A submitted or accepted e-invoice is corrected by credit note, never edited (document 07). */
export const isLocked = (r: EInvoiceRow | null | undefined): boolean =>
  r?.status === 'submitted' || r?.status === 'accepted';

export type EInvoiceFilter = 'exceptions' | 'all' | EInvoiceStatus;
export const FILTERS: Array<[EInvoiceFilter, string]> = [
  ['exceptions', 'Exceptions'],
  ['pending', 'Pending'],
  ['submitted', 'Submitted'],
  ['accepted', 'Accepted'],
  ['rejected', 'Rejected'],
  ['not_required', 'Not required'],
  ['all', 'All'],
];

export function matchesFilter(r: EInvoiceRow, f: EInvoiceFilter): boolean {
  if (f === 'all') return true;
  if (f === 'exceptions') return isException(r);
  return r.status === f;
}

/** One line per timeline event, in the accountant's words. */
export function describeEvent(e: EInvoiceEvent): { title: string; text: string | null } {
  const d = e.detail ?? {};
  const v = e.payloadVersion ? ` (version ${e.payloadVersion})` : '';
  switch (e.event) {
    case 'queued':
      return {
        title: 'Queued on approval',
        text: e.actorName ? `Approved by ${e.actorName}` : null,
      };
    case 'not_required':
      return { title: 'Not required', text: typeof d['reason'] === 'string' ? d['reason'] : null };
    case 'built': {
      const warnings = Number(d['warnings'] ?? 0);
      return {
        title: `E-invoice built${v}`,
        text: d['valid']
          ? `Passed validation${warnings ? ` with ${warnings} warning${warnings === 1 ? '' : 's'}` : ''}`
          : `${Number(d['errors'] ?? 0)} validation error${Number(d['errors']) === 1 ? '' : 's'}`,
      };
    }
    case 'validation_failed':
      return { title: 'Rejected by validation', text: errorsText(d['errors']) };
    case 'submitted':
      return {
        title: `Submitted to the service provider${v}`,
        text: typeof d['reference'] === 'string' ? `Reference ${d['reference']}` : null,
      };
    case 'delivery_failed':
      return {
        title: `Delivery attempt ${String(d['attempt'] ?? '')} failed`,
        text: [
          typeof d['error'] === 'string' ? d['error'] : null,
          typeof d['nextAttemptAt'] === 'string'
            ? `Next try ${formatDateTime(d['nextAttemptAt'])}`
            : 'No more automatic retries',
        ]
          .filter(Boolean)
          .join('. '),
      };
    case 'accepted':
      return { title: 'Accepted', text: null };
    case 'rejected':
      return { title: 'Rejected by the service provider', text: errorsText(d['errors']) };
    case 'resubmitted':
      return {
        title: 'Resubmitted',
        text: `${e.actorName ? `By ${e.actorName}. ` : ''}A new version is built from the current invoice and customer details.`,
      };
    case 'retried':
      return { title: 'Delivery retried', text: e.actorName ? `By ${e.actorName}` : null };
    case 'status_check_failed':
      return {
        title: 'Status check failed',
        text: typeof d['error'] === 'string' ? d['error'] : null,
      };
    default:
      return { title: e.event, text: null };
  }
}

function errorsText(v: unknown): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  return (v as EInvoiceError[]).map((e) => e.message).join('; ');
}

/** "14/10/2026 08:15" in the client's time zone. */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const timeZone = orgLocale().timeZone;
  const day = new Intl.DateTimeFormat('en-CA', { timeZone }).format(d);
  const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone });
  return `${formatDate(day)} ${time.format(d)}`;
}

// ---------------------------------------------------------------- queries

export function useEInvoicingSettings() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['einvoicing', orgId, 'settings'],
    queryFn: () => api<EInvoicingSettings>(`/orgs/${orgId}/einvoicing/settings`),
    enabled: Boolean(orgId),
  });
}

/** True only once the settings have loaded and e-invoicing is on: the UI shows nothing otherwise. */
export function useEInvoicingEnabled(): boolean {
  return useEInvoicingSettings().data?.enabled ?? false;
}

export function useSaveEInvoicingSettings() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      patch: Partial<Pick<EInvoicingSettings, 'enabled' | 'startDate' | 'legalId' | 'legalIdType'>>,
    ) =>
      api<EInvoicingSettings>(`/orgs/${orgId}/einvoicing/settings`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['einvoicing', orgId] });
    },
  });
}

/** Every e-invoice of the company (empty while e-invoicing is off). */
export function useEInvoices() {
  const orgId = useOrgId();
  const enabled = useEInvoicingEnabled();
  return useQuery({
    queryKey: ['einvoicing', orgId, 'submissions'],
    queryFn: () => api<EInvoiceRow[]>(`/orgs/${orgId}/einvoicing/submissions`),
    enabled: Boolean(orgId) && enabled,
    refetchInterval: 30_000,
  });
}

/** documentId → e-invoice, for status chips on lists; null while e-invoicing is off. */
export function useEInvoiceMap(): Map<string, EInvoiceRow> | null {
  const enabled = useEInvoicingEnabled();
  const rows = useEInvoices();
  if (!enabled) return null;
  return new Map((rows.data ?? []).map((r) => [r.documentId, r]));
}

export function useEInvoice(documentId: string | undefined) {
  const orgId = useOrgId();
  const enabled = useEInvoicingEnabled();
  return useQuery({
    queryKey: ['einvoicing', orgId, 'document', documentId],
    queryFn: () => api<EInvoiceDetail>(`/orgs/${orgId}/einvoicing/documents/${documentId}`),
    enabled: Boolean(orgId && documentId) && enabled,
    // follow the worker while the e-invoice is on its way
    refetchInterval: (q) => {
      const s = q.state.data?.submission;
      return s && (s.status === 'pending' || s.status === 'submitted') && !s.needsAttention
        ? 10_000
        : false;
    },
  });
}

export function useResubmit() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api<EInvoiceDetail>(`/orgs/${orgId}/einvoicing/documents/${documentId}/resubmit`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['einvoicing', orgId] });
    },
  });
}

/** Downloads one payload version's XML (the API sends it as an attachment). */
export async function downloadXml(orgId: string, payload: { id: string }, fileName: string) {
  const token = await auth.getAccessToken();
  const res = await fetch(`/api/orgs/${orgId}/einvoicing/payloads/${payload.id}/xml`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, 'HTTP', `Download failed (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
